import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { Field, FieldLabel, FormField, FormRow, FormModal, SearchSelect, Select, TextArea } from '../../components';
import { useCreateProperty, useUpdateProperty } from '../../queries/properties';
import { useAgentShortName } from '../../queries/agents';
import {
  COMMERCIAL_TYPES,
  HOME_TYPES,
  RESIDENTIAL_TYPES,
  SECTION8,
  STATUS,
  STATUS_OPTIONS,
  USES,
  formatMoney,
  roomsOf
} from './constants';

const YES_NO = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' }
];

const SAVE_ICON = <FontAwesomeIcon icon={faCheck} style={{ fontSize: 12 }} />;

const toForm = (l = {}) => {
  const use = l.use || 'residential';
  return {
    name: l.name || '',
    use,
    type: use === 'commercial' ? l.unitType : l._id ? roomsOf(l) : '2BR / 2BA',
    sqft: l.sqft == null ? '' : String(l.sqft),
    rent: formatMoney(l.rent) || '',
    ownerId: l.ownerId || '',
    tenantName: l.tenantName || '',
    status: l.status || 'available',
    openWork: l.openWork || '',
    homeType: l.use !== 'commercial' && l.unitType ? l.unitType : 'apartment',
    area: l.area || '',
    deposit: formatMoney(l.deposit) || '',
    availableDate: l.availableDate ? String(l.availableDate).slice(0, 10) : '',
    listed: l.listed === false ? 'no' : 'yes',
    section8: l.section8 || 'ask',
    pets: l.petsAllowed ? 'yes' : 'no',
    listingUrl: l.listingUrl || ''
  };
};

const toNumber = (v) => {
  const n = String(v).replace(/[^\d.]/g, '');
  return n ? Number(n) : null;
};

function toPayload(form, listing) {
  const tenantName = form.tenantName.trim();
  const base = {
    ...(listing?.tenantId && tenantName !== listing.tenantName && { tenantId: null }),
    name: form.name.trim(),
    use: form.use,
    rent: toNumber(form.rent),
    ownerId: form.ownerId || null,
    tenantName,
    status: form.status,
    openWork: form.openWork.trim(),
    area: form.area.trim(),
    deposit: toNumber(form.deposit),
    availableDate: form.availableDate || null,
    listed: form.listed === 'yes',
    section8: form.section8,
    petsAllowed: form.pets === 'yes',
    listingUrl: form.listingUrl.trim()
  };
  if (form.use === 'commercial') return { ...base, unitType: form.type, bedrooms: 0, sqft: toNumber(form.sqft) };
  const [bedrooms, bathrooms] = form.type === 'Studio' ? [0, 1] : form.type.match(/[\d.]+/g).map(Number);
  return { ...base, unitType: form.homeType, bedrooms, bathrooms };
}

const withCurrent = (options, value, label = value) =>
  !value || options.some((o) => o.value === value) ? options : [{ value, label }, ...options];

function Options({ options }) {
  return options.map((o) => (
    <option key={o.value} value={o.value}>
      {o.label}
    </option>
  ));
}

export default function PropertyFormModal({ listing, owners, onClose }) {
  const editing = Boolean(listing?._id);
  const agent = useAgentShortName();
  const [form, setForm] = useState(() => toForm(listing));
  const create = useCreateProperty();
  const update = useUpdateProperty();
  const valid = Boolean(form.name.trim());
  const commercial = form.use === 'commercial';

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setMoney = (key) => (e) => {
    const digits = e.target.value.replace(/\D/g, '');
    setForm((f) => ({ ...f, [key]: digits ? formatMoney(Number(digits)) : '' }));
  };
  const setUse = (e) => {
    const use = e.target.value;
    setForm((f) => ({ ...f, use, type: use === 'commercial' ? COMMERCIAL_TYPES[0].value : '2BR / 2BA' }));
  };

  const typeOptions = commercial
    ? withCurrent(COMMERCIAL_TYPES, form.type, 'Commercial')
    : withCurrent(RESIDENTIAL_TYPES, form.type);
  const ownerOptions = withCurrent(owners, form.ownerId, listing?.ownerName);
  const statusOptions = withCurrent(STATUS_OPTIONS, form.status, STATUS[form.status]?.label);

  const save = () => {
    const payload = toPayload(form, listing);
    if (editing) {
      update.mutate({ _id: listing._id, ...payload });
      onClose();
    } else {
      create.mutate(payload, { onSuccess: onClose });
    }
  };

  return (
    <FormModal
      title={editing ? `Edit ${listing.name}` : 'Add property'}
      subtitle={editing ? 'Update this property record' : 'New unit under management'}
      okText={editing ? 'Save changes' : 'Add property'}
      okIcon={SAVE_ICON}
      width={560}
      valid={valid}
      loading={create.isPending}
      onSubmit={save}
      onClose={onClose}
    >
      <FormField>
        <FieldLabel htmlFor="property-name">Address / unit</FieldLabel>
        <Field
          id="property-name"
          required
          autoFocus
          placeholder="e.g. 214 Magnolia Ct, Unit C"
          value={form.name}
          onChange={set('name')}
        />
      </FormField>
      <FormField>
        <FieldLabel htmlFor="property-use">Use</FieldLabel>
        <Select id="property-use" style={{ minWidth: 0 }} value={form.use} onChange={setUse}>
          <Options options={USES} />
        </Select>
      </FormField>
      {!commercial && (
        <FormField>
          <FieldLabel htmlFor="property-home-type">Home type</FieldLabel>
          <Select id="property-home-type" style={{ minWidth: 0 }} value={form.homeType} onChange={set('homeType')}>
            <Options options={HOME_TYPES} />
          </Select>
        </FormField>
      )}
      <FormRow>
        <FormField>
          <FieldLabel htmlFor="property-type">{commercial ? 'Type' : 'Layout'}</FieldLabel>
          <Select id="property-type" style={{ minWidth: 0 }} value={form.type} onChange={set('type')}>
            <Options options={typeOptions} />
          </Select>
        </FormField>
        <FormField>
          <FieldLabel htmlFor="property-rent">Monthly rent</FieldLabel>
          <Field id="property-rent" inputMode="numeric" placeholder="$1,450" value={form.rent} onChange={setMoney('rent')} />
        </FormField>
      </FormRow>
      <FormRow>
        <FormField>
          <FieldLabel htmlFor="property-area">Area / neighborhood</FieldLabel>
          <Field id="property-area" placeholder="e.g. Moss Bluff" value={form.area} onChange={set('area')} />
        </FormField>
        <FormField>
          <FieldLabel htmlFor="property-deposit">Deposit</FieldLabel>
          <Field id="property-deposit" inputMode="numeric" placeholder="$1,450" value={form.deposit} onChange={setMoney('deposit')} />
        </FormField>
      </FormRow>
      {commercial && (
        <FormField>
          <FieldLabel htmlFor="property-sqft">Square feet</FieldLabel>
          <Field id="property-sqft" inputMode="numeric" placeholder="e.g. 2,400" value={form.sqft} onChange={set('sqft')} />
        </FormField>
      )}
      <FormField>
        <FieldLabel htmlFor="property-owner">Owner</FieldLabel>
        <SearchSelect
          id="property-owner"
          allowClear
          placeholder="Select an owner"
          value={form.ownerId}
          onChange={(v) => setForm((f) => ({ ...f, ownerId: v }))}
          options={ownerOptions}
        />
      </FormField>
      <FormField>
        <FieldLabel htmlFor="property-tenant">Current tenant</FieldLabel>
        <Field id="property-tenant" placeholder="Leave blank if vacant" value={form.tenantName} onChange={set('tenantName')} />
      </FormField>
      <FormField>
        <FieldLabel htmlFor="property-status">Status</FieldLabel>
        <Select id="property-status" style={{ minWidth: 0 }} value={form.status} onChange={set('status')}>
          <Options options={statusOptions} />
        </Select>
      </FormField>
      <FormRow>
        <FormField>
          <FieldLabel htmlFor="property-date">{form.status === 'coming_soon' ? 'Expected date' : 'Available date'}</FieldLabel>
          <Field id="property-date" type="date" value={form.availableDate} onChange={set('availableDate')} />
        </FormField>
        <FormField>
          <FieldLabel htmlFor="property-listed">On the website</FieldLabel>
          <Select id="property-listed" style={{ minWidth: 0 }} value={form.listed} onChange={set('listed')}>
            <option value="yes">Listed — {agent} can quote it</option>
            <option value="no">Not listed — {agent} won't offer it</option>
          </Select>
        </FormField>
      </FormRow>
      {!commercial && (
        <FormRow>
          <FormField>
            <FieldLabel htmlFor="property-section8">Section 8</FieldLabel>
            <Select id="property-section8" style={{ minWidth: 0 }} value={form.section8} onChange={set('section8')}>
              <Options options={SECTION8} />
            </Select>
          </FormField>
          <FormField>
            <FieldLabel htmlFor="property-pets">Pets allowed</FieldLabel>
            <Select id="property-pets" style={{ minWidth: 0 }} value={form.pets} onChange={set('pets')}>
              <Options options={YES_NO} />
            </Select>
          </FormField>
        </FormRow>
      )}
      <FormField>
        <FieldLabel htmlFor="property-listing-url">Listing page link</FieldLabel>
        <Field id="property-listing-url" type="url" placeholder="https://" value={form.listingUrl} onChange={set('listingUrl')} />
      </FormField>
      <FormField>
        <FieldLabel htmlFor="property-note">Open work</FieldLabel>
        <TextArea
          id="property-note"
          placeholder="Turn work, inspections, lease dates…"
          value={form.openWork}
          onChange={set('openWork')}
        />
      </FormField>
    </FormModal>
  );
}
